import React, { useEffect } from 'react';
import _get from 'lodash/get';
import { useFormikContext } from 'formik';
import { useRouter } from 'next/router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FLAGS } from 'legacy/common/constants/launchdarkly';

import Guests from './Guests';
import Organizers from './Organizers';

import {
  useUploadPhotoCommand,
  useUploadHypeVideoCommand
} from 'legacy/events/hooks';
import FadeIn from 'legacy/common/components/transition/FadeIn';
import Title from 'legacy/common/components/wysiwyg-form/Title';
import Divider from 'legacy/common/components/wysiwyg-form/Divider';
import Location from 'legacy/common/components/wysiwyg-form/Location';
import DateTime from 'legacy/common/components/wysiwyg-form/DateTime';
import ImageUpload from 'legacy/common/components/wysiwyg-form/ImageUpload';
import VideoUpload from 'legacy/common/components/wysiwyg-form/VideoUpload';
import Description from 'legacy/common/components/wysiwyg-form/Description';
import PrivateToggle from 'legacy/common/components/wysiwyg-form/PrivateToggle';
import { Event } from 'legacy/common/types/models';
import { useVideoUploadCommand } from 'legacy/common/hooks/useVideoUpload';
import RecurringEvent from 'legacy/common/components/wysiwyg-form/RecurringEvent';
import RecurringEventBadge from '../event/RecurringEventBadge';

interface BasicFormProps {
  eventId?: string;
  navigateToRecurringSettings: () => void;
  createDraft: (values?: any) => Promise<Event>;
}

const BasicForm = ({
  eventId,
  createDraft,
  navigateToRecurringSettings
}: BasicFormProps) => {
  const flags = useFlags();
  const uploadPhoto = useUploadPhotoCommand();
  const uploadHypeVideo = useUploadHypeVideoCommand();
  const { isUploading, useVideoUpload } = useVideoUploadCommand();
  const { values, errors, setFieldValue, setFieldError } =
    useFormikContext<any>();

  const [hasEndDate, setHasEndDate] = React.useState(false);

  const router = useRouter();
  const { startDate } = router.query; // Extract startDate from query params

  useEffect(() => {
    if (values.endDate) {
      setHasEndDate(true);
    }
  }, [values.endDate]);

  const toggleEndDate = () => {
    setHasEndDate((hasEndDate) => !hasEndDate);
  };

  const onUploadPhoto = async (blob: Blob) => {
    let newEventId = eventId;
    // Create a draft if event has not been created yet
    if (!newEventId) {
      const event = await createDraft();
      newEventId = event._id;
    }

    uploadPhoto.mutate(
      { eventId: newEventId, blob },
      {
        onSuccess: ({ photo }) => {
          setFieldValue('photo.url', photo?.url);
        }
      }
    );
  };

  const onUploadVideo = async (file: File) => {
    let newEventId = eventId;
    // Create a draft if event has not been created yet
    if (!newEventId) {
      const event = await createDraft();
      newEventId = event._id;
    }

    const videoId = await useVideoUpload(file);

    uploadHypeVideo.mutate(
      { eventId: newEventId, videoId },
      {
        onSuccess: ({ hypeVideoUrl }) => {
          setFieldValue('hypeVideoUrl', hypeVideoUrl);
        }
      }
    );
  };

  return (
    <FadeIn>
      <div className="md:mt-[47px] md:w-[630px] md:mx-auto pb-20 -mt-[40px]">
        <div className="absolute z-10 m-4">
          <RecurringEventBadge eventId={eventId} seriesId={values.seriesId} />
        </div>
        {/* Form Content */}
        <VideoUpload
          className="w-full"
          videoUrl={values?.hypeVideoUrl}
          onUploadVideo={onUploadVideo}
          isUploading={isUploading || uploadHypeVideo.isLoading}
          error={_get(errors, 'hypeVideo.url')}
          onSetError={(error) => setFieldError('hypeVideo.url', error)}
        />
        <ImageUpload
          className="md:-mt-[240px] ml-4 w-28 md:w-[140px] -mt-[340px]"
          photoUrl={values?.photo?.url}
          isUploading={uploadPhoto.isLoading}
          onUploadPhoto={onUploadPhoto}
          error={_get(errors, 'photo.url')}
          onSetError={(error) => error && setFieldError('photo.url', error)}
        />
        <div className="w-full px-4 mx-auto relative">
          <Title />
          <Location />
          <DateTime
            name="startDate"
            datePlaceholder="Start date*"
            timePlaceholder="Start time*"
            min={new Date()}
            max={values.endDate}
            startDate={startDate as string}
          />

          {hasEndDate ? (
            <DateTime
              name="endDate"
              datePlaceholder="End date"
              timePlaceholder="End time"
              min={values.startDate}
            />
          ) : (
            <button
              type="button"
              onClick={toggleEndDate}
              className="mt-4 text-primary-500 text-base font-semibold"
            >
              Does it end?
            </button>
          )}

          {flags[FLAGS.ENABLE_RECURRING_EVENTS] && (
            <RecurringEvent
              eventId={eventId}
              navigateToRecurringSettings={navigateToRecurringSettings}
            />
          )}

          <PrivateToggle />

          <Divider />
          <Organizers />
          <Guests />
          <Divider />

          <Description />
        </div>
      </div>
    </FadeIn>
  );
};

export default BasicForm;
